import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, Box, Group, Menu, Paper, Stack, Text, Tooltip, UnstyledButton} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as NavConstants from 'constants/NavConstants';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import staticURL from 'lib/staticURL';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import {RootCrumb} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import ToggleDropdown from 'nav/ToggleDropdown';
import notifications, {Response} from 'notifications';
import EntityStore from 'stores/EntityStore';
import {TENANT_ADMIN_PAGE_TAB_TO_SECTION_URL_PARAM, TenantAdminTab} from 'tenants/admin/TenantAdminConstants';
import LegacyTooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as nyc from 'ui-library/NYC';
import * as modals from 'util/Modals';
import {deletePromise, isServerRequestError} from 'util/Net';

import {MCPSidepanelQuery} from './__generated/MCPSidepanelQuery';
import ConnectMCPModal from './ConnectMCPModal';
import {mcpAdminHasGrantedAtLeastOneTool} from './mcpGrantedTools';
import {useMCPPing} from './useMCPPing';
import UserToolPermissionsModal from './UserMcpConfigurationModal';

/**
 * Prompts for confirmation then uninstalls an MCP server config. Used from both
 * the connections sidepanel and the TAC.
 */
export async function deleteMcpServerConfig({
  configId,
  configName,
  onSuccess,
}: {
  configId: string;
  configName: string;
  onSuccess: () => void;
}): Promise<void> {
  const confirmed = (await Promise.resolve(
    modals.confirm(
      `Uninstall ${configName}`,
      `Uninstalling ${configName} will remove it and any related configurations.`,
      ButtonStyle.NEGATIVE,
      {acceptText: 'Uninstall', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
    )
  )) as boolean;
  if (!confirmed) {
    return;
  }
  try {
    await deletePromise({url: `/1/api/llm/mcp-server-configs/${configId}`});
    notifications.success(`${configName} uninstalled successfully.`);
    onSuccess();
  } catch (err: unknown) {
    if (isServerRequestError(err)) {
      notifications.notifyError(err as Response);
    } else {
      throw err;
    }
  }
}

export const MCP_SIDEPANEL_QUERY = gql`
  query MCPSidepanelQuery @skipAudit @engComponent(name: LLM) {
    mcpServerConfigs {
      id
      name
      description
      authType
      extensionDefinitionId
      iconUrl
      toolPermissions {
        permission
      }
    }
    mcpServerUserConfigs {
      id
      mcpServerConfig {
        id
        name
        description
        authType
        extensionDefinitionId
        iconUrl
      }
    }
  }
`;

interface MCPSidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
  connectionsTab: JSX.Element;
}

type MCPServerConfig = MCPSidepanelQuery['mcpServerConfigs'][number];
type MCPServerUserConfig = MCPSidepanelQuery['mcpServerUserConfigs'][number];

interface ConnectedCardProps {
  config: MCPServerUserConfig;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
}

interface AvailableCardProps {
  config: MCPServerConfig;
  onConnect: (config: MCPServerConfig) => void;
}

function McpServerIcon({iconPath}: {iconPath: string | null}): JSX.Element {
  if (iconPath != null) {
    return <img src={staticURL(iconPath)} alt="" className={css(styles.mcpIconImg)} />;
  }
  // TODO(BNCH-151518): fall back to the icon from the MCP `initialize` response.
  return <BenchlingIcon icon="sources" />;
}

function ConnectedCard(props: ConnectedCardProps): JSX.Element {
  const {config, onDisconnect, onManageUserTools} = props;
  const {id, name, description, iconUrl: icon} = config.mcpServerConfig;

  const {
    status: pingStatus,
    isLoading: isPinging,
    refetch: refetchPingStatus,
  } = useMCPPing({
    serverId: id,
    enabled: true,
    pollingInterval: 5 * 60 * 1000, // 5 minutes
  });

  let statusText = 'CHECKING...';
  let statusStyle = styles.statusBadgeChecking;
  let isAuthRequired = false;
  let statusTooltip: string | undefined;

  if (isPinging && !pingStatus) {
    statusText = 'CHECKING...';
    statusStyle = styles.statusBadgeChecking;
  } else if (pingStatus?.isConnected) {
    statusText = 'CONNECTED';
    statusStyle = styles.statusBadgeConnected;
  } else if (pingStatus?.needsRedirect) {
    statusText = 'AUTH REQUIRED';
    statusStyle = styles.statusBadgeAuthRequired;
    isAuthRequired = true;
  } else {
    statusText = 'DISCONNECTED';
    statusStyle = styles.statusBadgeDisconnected;
    statusTooltip = pingStatus?.errorMessage ?? undefined;
  }

  const handleAuthClick = (): void => {
    if (pingStatus?.redirectUrl) {
      const authWindow = window.open(pingStatus.redirectUrl, '_blank');

      if (authWindow) {
        const pollInterval = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(pollInterval);
            refetchPingStatus();
          }
        }, 500);
      }
    }
  };

  return (
    <Paper className={css(styles.card)} p={0} radius={4} withBorder={true}>
      <Group noWrap={true} position="apart" align="center" className={css(styles.cardInner)}>
        <Group noWrap={true} spacing={16} align="center" style={{minWidth: 0, flex: 1}}>
          <Box className={css(styles.mcpIcon)}>
            <McpServerIcon iconPath={icon} />
          </Box>
          <Stack spacing={8} style={{minWidth: 0, width: 173}}>
            <Stack spacing={2}>
              <Text className={css(styles.cardTitle)} lineClamp={1}>
                {name}
              </Text>
              {description && (
                <Text className={css(styles.cardMetaText)} lineClamp={1}>
                  {description}
                </Text>
              )}
            </Stack>
            <Tooltip label={statusTooltip} disabled={!statusTooltip} withinPortal={true}>
              {isAuthRequired ? (
                <UnstyledButton
                  className={css(styles.statusBadge, statusStyle, styles.statusBadgeClickable)}
                  onClick={handleAuthClick}
                  aria-label={`${statusText} - click to re-authenticate`}
                >
                  {statusText}
                </UnstyledButton>
              ) : (
                <div className={css(styles.statusBadge, statusStyle)}>{statusText}</div>
              )}
            </Tooltip>
          </Stack>
        </Group>
        <Group noWrap={true} spacing={4} align="center" className={css(styles.cardActions)}>
          <Menu position="bottom-start" withinPortal={true}>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                size="sm"
                className={css(styles.menuButton)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <BenchlingIcon icon="ellipsis-vertical" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  onManageUserTools(config);
                }}
              >
                Manage tools
              </Menu.Item>
              <Menu.Item
                color="red"
                onClick={() => {
                  onDisconnect(config);
                }}
              >
                Disconnect
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Paper>
  );
}

/** A server is usable when at least one tool has been granted by an admin. */
function hasGrantedTools(config: MCPServerConfig): boolean {
  return mcpAdminHasGrantedAtLeastOneTool(config.toolPermissions);
}

function AvailableCard(props: AvailableCardProps): JSX.Element {
  const {config, onConnect} = props;
  const {name, description, iconUrl: icon} = config;

  return (
    <Paper className={css(styles.card)} p={0} radius={4} withBorder={true}>
      <Group noWrap={true} position="apart" align="center" className={css(styles.cardInner)}>
        <Group noWrap={true} spacing={16} align="center" style={{minWidth: 0, flex: 1}}>
          <Box className={css(styles.mcpIcon)}>
            <McpServerIcon iconPath={icon} />
          </Box>
          <Stack spacing={2} style={{minWidth: 0, width: 173}}>
            <Text className={css(styles.cardTitle)} lineClamp={1}>
              {name}
            </Text>
            {description && (
              <Text className={css(styles.cardMetaText)} lineClamp={1}>
                {description}
              </Text>
            )}
          </Stack>
        </Group>
        <Group noWrap={true} spacing={4} align="center" className={css(styles.cardActions)}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.SMALL}
            text="Connect"
            onClick={() => {
              onConnect(config);
            }}
          />
        </Group>
      </Group>
    </Paper>
  );
}

function AwaitingSetupCard({config}: {config: MCPServerConfig}): JSX.Element {
  const {name, description, iconUrl: icon} = config;

  return (
    <Tooltip
      label="An admin needs to grant tool access before this server can be connected"
      withinPortal={true}
      position="top"
    >
      <Paper className={css(styles.card, styles.cardAwaiting)} p={0} radius={4} withBorder={true}>
        <Group noWrap={true} position="apart" align="center" className={css(styles.cardInner)}>
          <Group noWrap={true} spacing={16} align="center" style={{minWidth: 0, flex: 1}}>
            <Box className={css(styles.mcpIcon, styles.mcpIconDisabled)}>
              <McpServerIcon iconPath={icon} />
            </Box>
            <Stack spacing={2} style={{minWidth: 0, width: 173}}>
              <Text className={css(styles.cardTitle, styles.cardTitleAwaiting)} lineClamp={1}>
                {name}
              </Text>
              {description && (
                <Text className={css(styles.cardMetaText)} lineClamp={1}>
                  {description}
                </Text>
              )}
            </Stack>
          </Group>
          <Group noWrap={true} spacing={4} align="center" className={css(styles.cardActions)}>
            <div className={css(styles.awaitingBadge)}>Setup required</div>
          </Group>
        </Group>
      </Paper>
    </Tooltip>
  );
}

interface MCPSidepanelHeaderProps extends MCPSidepanelProps {
  onOpenDirectory: () => void;
  showDirectoryButton: boolean;
}

function MCPSidepanelHeader(props: MCPSidepanelHeaderProps): JSX.Element {
  return (
    <ListingHeader
      buttons={
        <div className="sidepanel-header-buttons u-flex-display">
          <ToggleDropdown expanded={props.isExpanded} />
          {props.showDirectoryButton && (
            <LegacyTooltip title="Browse MCP directory" placement={TooltipPlacement.BOTTOM} container="body">
              <Button
                aria-label="Browse MCP directory"
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                icon="compose"
                onClick={props.onOpenDirectory}
                size={ButtonSize.SMALL}
              />
            </LegacyTooltip>
          )}
        </div>
      }
      crumbs={<RootCrumb text={MCP_PRODUCT_NAME_PLURAL} />}
      isExpanded={props.isExpanded}
      queryInput={<div />}
      filtersContainer={<div />}
      sectionTabs={props.connectionsTab}
    />
  );
}

interface MCPSidepanelContentsProps extends MCPSidepanelProps {
  connectedConfigs: Array<MCPServerUserConfig>;
  availableConfigs: Array<MCPServerConfig>;
  awaitingSetupConfigs: Array<MCPServerConfig>;
  isLoading: boolean;
  onConnect: (config: MCPServerConfig) => void;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
  onOpenDirectory: () => void;
  showDirectoryButton: boolean;
}

function CollapsedMCPSidepanelContents(props: MCPSidepanelContentsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    awaitingSetupConfigs,
    onConnect,
    onDisconnect,
    onManageUserTools,
  } = props;
  const isEmpty =
    connectedConfigs.length === 0 && availableConfigs.length === 0 && awaitingSetupConfigs.length === 0;

  return (
    <>
      <nyc.Row>
        <MCPSidepanelHeader {...props} />
      </nyc.Row>
      <nyc.Row
        aria-labelledby={`${NavConstants.Section.MCPS}_TAB`}
        className="u-vertical-flex"
        id={`${NavConstants.Section.MCPS}_PANEL`}
        fluid={true}
        role="tabpanel"
      >
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">
            {props.isLoading ? (
              <CenteredHorizontalThrobber />
            ) : (
              <div className={css(styles.cardContainer)}>
                {isEmpty ? (
                  <div className="sidepanel-fadedMessage">
                    Add {MCP_PRODUCT_NAME_PLURAL} to reference other tools and services
                  </div>
                ) : (
                  <GroupedMCPConfigs
                    connectedConfigs={connectedConfigs}
                    availableConfigs={availableConfigs}
                    awaitingSetupConfigs={awaitingSetupConfigs}
                    onConnect={onConnect}
                    onDisconnect={onDisconnect}
                    onManageUserTools={onManageUserTools}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </nyc.Row>
    </>
  );
}

function ExpandedMCPSidepanelContents(props: MCPSidepanelContentsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    awaitingSetupConfigs,
    onConnect,
    onDisconnect,
    onManageUserTools,
  } = props;
  const isEmpty =
    connectedConfigs.length === 0 && availableConfigs.length === 0 && awaitingSetupConfigs.length === 0;

  return (
    <nyc.Row fluid={true}>
      <nyc.Col lg={1} />
      <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
        <MCPSidepanelHeader {...props} />
        <div
          aria-labelledby={NavConstants.Section.MCPS}
          className="u-flex-grow u-flex-display u-small-spacing-top"
          role="tabpanel"
        >
          {props.isLoading ? (
            <CenteredHorizontalThrobber />
          ) : (
            <div className={css(styles.expandedContainer)}>
              {isEmpty ? (
                <div className="sidepanel-fadedMessage">
                  Add {MCP_PRODUCT_NAME_PLURAL} to reference other tools and services for more context in AI
                  Experiences
                </div>
              ) : (
                <GroupedMCPConfigs
                  connectedConfigs={connectedConfigs}
                  availableConfigs={availableConfigs}
                  awaitingSetupConfigs={awaitingSetupConfigs}
                  onConnect={onConnect}
                  onDisconnect={onDisconnect}
                  onManageUserTools={onManageUserTools}
                />
              )}
            </div>
          )}
        </div>
      </nyc.Col>
      <nyc.Col lg={1} />
    </nyc.Row>
  );
}

interface GroupedMCPConfigsProps {
  connectedConfigs: Array<MCPServerUserConfig>;
  availableConfigs: Array<MCPServerConfig>;
  awaitingSetupConfigs: Array<MCPServerConfig>;
  onConnect: (config: MCPServerConfig) => void;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
}

const TAC_MCP_URL = `/admin/${TENANT_ADMIN_PAGE_TAB_TO_SECTION_URL_PARAM[TenantAdminTab.EXTENSIONS_TAB]}/mcps`;

/** Renders MCP configs grouped by connection state with a divider between sections */
function GroupedMCPConfigs(props: GroupedMCPConfigsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    awaitingSetupConfigs,
    onConnect,
    onDisconnect,
    onManageUserTools,
  } = props;
  const isAdmin = Boolean(EntityStore.currentUser()?.isTenantAdmin);

  return (
    <>
      {connectedConfigs.length > 0 && (
        <div className={css(styles.section)}>
          <Text className={css(styles.sectionHeader)}>Connected</Text>
          <Text className={css(styles.sectionSubheader)}>
            These have been connected for you to use in your AI chats.
          </Text>
          <div className={css(styles.cardsGrid)}>
            {connectedConfigs.map((config) => (
              <ConnectedCard
                key={config.id}
                config={config}
                onDisconnect={onDisconnect}
                onManageUserTools={onManageUserTools}
              />
            ))}
          </div>
        </div>
      )}
      {connectedConfigs.length > 0 && availableConfigs.length > 0 && <div className={css(styles.divider)} />}
      {availableConfigs.length > 0 && (
        <div className={css(styles.section)}>
          <Text className={css(styles.sectionHeader)}>Available</Text>
          <Text className={css(styles.sectionSubheader)}>
            These have been installed and configured by your admin. Connect to start using them.
          </Text>
          <div className={css(styles.cardsGrid)}>
            {availableConfigs.map((config) => (
              <AvailableCard key={config.id} config={config} onConnect={onConnect} />
            ))}
          </div>
        </div>
      )}
      {(connectedConfigs.length > 0 || availableConfigs.length > 0) && awaitingSetupConfigs.length > 0 && (
        <div className={css(styles.divider)} />
      )}
      {awaitingSetupConfigs.length > 0 && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionHeaderRow)}>
            <Text className={css(styles.sectionHeader)}>Awaiting Setup</Text>
            {isAdmin && (
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.SMALL}
                text="Configure in Admin Settings →"
                onClick={() => {
                  navigateToURL(TAC_MCP_URL);
                }}
              />
            )}
          </div>
          <Text className={css(styles.sectionSubheader)}>
            {isAdmin
              ? 'These servers need tool access configured before users can connect. Go to Admin Settings to finish setup.'
              : 'These servers are installed but an admin needs to configure tool access before you can connect.'}
          </Text>
          <div className={css(styles.cardsGrid)}>
            {awaitingSetupConfigs.map((config) => (
              <AwaitingSetupCard key={config.id} config={config} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default function MCPSidepanel(props: MCPSidepanelProps): JSX.Element {
  const [connectConfig, setConnectConfig] = useState<MCPServerConfig | null>(null);
  const [userToolsConfig, setUserToolsConfig] = useState<MCPServerUserConfig | null>(null);
  const queryResult = useBenchlingQuery<MCPSidepanelQuery>(MCP_SIDEPANEL_QUERY, {
    // Keep showing the connector list while refetching (e.g. after adding a server) so ConnectedCard
    // stays mounted and useMCPPing does not reset every MCP to "checking" / pending.
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });
  const prevExpandedRef = useRef(props.isExpanded);
  const isTenantAdmin = Boolean(EntityStore.currentUser()?.isTenantAdmin);
  const showDirectoryButton = BenchlingConfig.ENABLE_EXTENSION_DIRECTORY && !isTenantAdmin;

  // Navigate to /ai-connectors when expanded
  useEffect(() => {
    if (props.isExpanded) {
      navigateToURL('/ai-connectors', {
        skipTrigger: true,
        replace: prevExpandedRef.current === props.isExpanded,
      });
    }
    prevExpandedRef.current = props.isExpanded;
  }, [props.isExpanded]);

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const isErrored = queryResult.type === QueryResultType.ERROR;
  let connectedConfigs: Array<MCPServerUserConfig> = [];
  let availableConfigs: Array<MCPServerConfig> = [];
  let awaitingSetupConfigs: Array<MCPServerConfig> = [];

  if (isErrored) {
    notifications.error(`Could not load ${MCP_PRODUCT_NAME_PLURAL}; try reloading the page.`);
  } else if (queryResult.type === QueryResultType.SUCCESS) {
    connectedConfigs = queryResult.data.mcpServerUserConfigs;
    const connectedServerIds = new Set(connectedConfigs.map((uc) => uc.mcpServerConfig.id));
    const notConnected = queryResult.data.mcpServerConfigs.filter((sc) => !connectedServerIds.has(sc.id));
    availableConfigs = notConnected.filter((sc) => hasGrantedTools(sc));
    awaitingSetupConfigs = notConnected.filter((sc) => !hasGrantedTools(sc));
  }

  const handleConnect = (config: MCPServerConfig): void => {
    setConnectConfig(config);
  };
  const handleConnectModalClose = (): void => {
    setConnectConfig(null);
  };
  const handleConnectModalSubmit = (userConfigId: string): void => {
    const serverConfig = connectConfig;
    setConnectConfig(null);
    queryResult.refetch();

    if (serverConfig) {
      setUserToolsConfig({
        __typename: 'McpServerUserConfigNode',
        id: userConfigId,
        mcpServerConfig: serverConfig,
      });
    }
  };

  const handleDisconnect = async (config: MCPServerUserConfig): Promise<void> => {
    try {
      await deletePromise({
        url: `/1/api/llm/mcp-server-user-configs/${config.id}`,
      });
      notifications.success(`${config.mcpServerConfig.name} disconnected successfully`);
      queryResult.refetch();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error(`Could not disconnect ${config.mcpServerConfig.name}`);
      } else {
        throw e;
      }
    }
  };

  const handleManageUserTools = (config: MCPServerUserConfig): void => {
    setUserToolsConfig(config);
  };
  const handleUserToolsModalClose = (): void => {
    setUserToolsConfig(null);
  };
  const handleUserToolsModalSubmit = (): void => {
    setUserToolsConfig(null);
    queryResult.refetch();
  };

  const handleOpenDirectory = (): void => {
    legacyRunModal(ContentStoreModal, {
      extensionType: ExtensionType.MCP_SERVER,
      onInstall: queryResult.refetch,
    });
  };

  return (
    <>
      {connectConfig && (
        <ConnectMCPModal
          config={connectConfig}
          onRequestClose={handleConnectModalClose}
          onRequestSubmit={handleConnectModalSubmit}
        />
      )}
      {userToolsConfig && (
        <MCPToolMappingProvider>
          <UserToolPermissionsModal
            userConfigId={userToolsConfig.id}
            serverId={userToolsConfig.mcpServerConfig.id}
            serverName={userToolsConfig.mcpServerConfig.name}
            onRequestClose={handleUserToolsModalClose}
            onRequestSubmit={handleUserToolsModalSubmit}
          />
        </MCPToolMappingProvider>
      )}
      <nyc.Container
        className={classNames('sidepanel', {expandedContainer: props.isExpanded})}
        size={props.isExpanded ? 'lg' : 'sidepanel'}
      >
        {props.isExpanded ? (
          <ExpandedMCPSidepanelContents
            {...props}
            connectedConfigs={connectedConfigs}
            availableConfigs={availableConfigs}
            awaitingSetupConfigs={awaitingSetupConfigs}
            isLoading={isLoading}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onManageUserTools={handleManageUserTools}
            onOpenDirectory={handleOpenDirectory}
            showDirectoryButton={showDirectoryButton}
          />
        ) : (
          <CollapsedMCPSidepanelContents
            {...props}
            connectedConfigs={connectedConfigs}
            availableConfigs={availableConfigs}
            awaitingSetupConfigs={awaitingSetupConfigs}
            isLoading={isLoading}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onManageUserTools={handleManageUserTools}
            onOpenDirectory={handleOpenDirectory}
            showDirectoryButton={showDirectoryButton}
          />
        )}
      </nyc.Container>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    cursor: 'pointer',
    width: 350,
    height: 80,
    overflow: 'hidden',
    transition: 'box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out',
    backgroundColor: Color.WHITE,
    borderColor: Color.GRAY_200,
    ':hover': {
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
      borderColor: Color.GRAY_300,
    },
  },
  cardInner: {
    padding: '0 16px',
    height: '100%',
  },
  cardTitle: {
    color: Color.GRAY_900,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
  },
  cardMetaText: {
    color: Color.GRAY_700,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mcpIcon: {
    width: 30,
    height: 30,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    border: `1px solid ${Color.GRAY_200}`,
  },
  mcpIconImg: {
    width: 30,
    height: 30,
    objectFit: 'contain' as const,
  },
  mcpIconDisabled: {
    opacity: 0.8,
    borderColor: 'rgba(226, 227, 229, 0.8)',
  },
  statusBadge: {
    width: 'fit-content',
    textTransform: 'uppercase',
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '12px',
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_800,
    padding: '2px 6px',
    borderRadius: 4,
  },
  statusBadgeConnected: {
    backgroundColor: Color.GREEN_200,
    color: Color.GREEN_600,
  },
  statusBadgeDisconnected: {
    backgroundColor: Color.RED_150,
    color: Color.RED_600,
  },
  statusBadgeChecking: {
    backgroundColor: Color.YELLOW_200,
    color: Color.YELLOW_600,
  },
  statusBadgeAuthRequired: {
    backgroundColor: Color.YELLOW_200,
    color: Color.YELLOW_600,
  },
  statusBadgeClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Color.YELLOW_250,
      textDecoration: 'underline',
    },
  },
  menuButton: {
    color: Color.BLUE_500,
    padding: '4px 8px',
  },
  cardActions: {
    flexShrink: 0,
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    padding: 12,
    width: '100%',
  },
  expandedContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    padding: 12,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 4,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: 600,
    color: Color.GRAY_600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 2,
    display: 'block',
  },
  sectionSubheader: {
    fontSize: 12,
    color: Color.GRAY_600,
    marginBottom: 8,
    display: 'block',
  },
  cardAwaiting: {
    cursor: 'default',
    opacity: 0.6,
    ':hover': {
      boxShadow: 'none',
      borderColor: Color.GRAY_200,
    },
  },
  cardTitleAwaiting: {
    color: Color.GRAY_600,
  },
  awaitingBadge: {
    fontSize: 11,
    fontWeight: 500,
    color: Color.GRAY_600,
    backgroundColor: Color.GRAY_100,
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: 4,
    padding: '2px 8px',
    whiteSpace: 'nowrap',
  },
  cardsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: '12px 16px',
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: Color.GRAY_200,
    margin: '20px 0',
  },
});
